import "./style.scss";
import { useCallback, useEffect, useState } from "react";
import { admissionsOfficeHeaderTabsConst } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAdmissionFormContext,
  setAdmissionFormContext,
  setTabsAdmissionsOffice,
} from "@/src/store/slices/AdmissionOffice/admissionOfficeDocSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SelectDefault from "../../SelectDefault/SelectDefault";
import BtnDefault from "../../BtnDefault/BtnDefault";
import Refresh from "@/src/svg/header/Refresh";
import DatabaseSvg from "@/src/svg/admissionOfficePage/DatabaseSvg";
import VerticalLine from "../../VerticalLine/VerticalLine";
import CustomFullNamePopUp from "../../CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { RootState } from "@/src/store";
import { IRowData } from "@/src/types/applicationOnline";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { usePushNotifications } from "../../CustomUtils/PushNotifications/PushNotificationsProvider";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { parseTaskApplicantId, parseTaskApplicantInfo } from "@/src/utils/tasks/parseTaskApplicantInfo";

type AdmissionsLang = "en" | "fr" | "de";

const parsePositiveId = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const HeaderAdmissionsOffice = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = usePushNotifications();
  const { taskId } = useSelector((state: RootState) => state.admissionOfficeDoc);
  const [currentTab, setCurrentTab] = useState<string>("");
  const [currentSelect, setCurrentSelect] = useState<string>("");
  const [domTaskId, setDomTaskId] = useState("");
  const [isApPopUpFullName, setIsApPopUpFullName] = useState(false);
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentApplyInfoId, setCurrentApplyInfoId] = useState<number | undefined>();

  const effectiveTaskId = domTaskId || taskId;
  const isUserInfoDisabled = !effectiveTaskId.trim();

  const readTaskIdFromDom = useCallback(() => {
    const taskInput = document.querySelector("input#task_id") as HTMLInputElement | null;
    const nextTaskId = taskInput?.value.trim() ?? "";
    setDomTaskId(nextTaskId);
    if (nextTaskId) {
      dispatch(setAdmissionFormContext({ taskId: nextTaskId }));
    }
    return nextTaskId;
  }, [dispatch]);

  const parseAdmissionsOfficeUrl = (pathname: string) => {
    const parts = pathname.split("/").filter(Boolean);

    if (parts[0] !== "admissions-office") return null;

    let lang: AdmissionsLang = "en";
    let country: string | undefined;
    let slug: string | undefined;

    if (parts[1] === "fr" || parts[1] === "de") {
      lang = parts[1];
      country = parts[2];
      slug = parts[3];
    } else {
      country = parts[1];
      slug = parts[2];
    }

    if (!country || !slug) return null;

    const tabObj = admissionsOfficeHeaderTabsConst.find((t) => t.id === country);
    if (!tabObj) return null;

    const option = tabObj.mass.find((m) => m.slug === slug && (m.lang ?? "en") === lang);
    if (!option) return null;

    return {
      tabId: tabObj.id,
      selectName: option.name,
      lang,
    };
  };

  useEffect(() => {
    const parsed = parseAdmissionsOfficeUrl(location.pathname);
    if (!parsed) return;

    setCurrentTab(parsed.tabId);
    setCurrentSelect(parsed.selectName);
    dispatch(setTabsAdmissionsOffice(parsed.tabId));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    dispatch(clearAdmissionFormContext());
    setDomTaskId("");

    readTaskIdFromDom();
    const syncTimeouts = [100, 400, 1000, 2000].map((delay) => window.setTimeout(readTaskIdFromDom, delay));

    const onInput = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.id === "task_id") {
        const nextTaskId = target.value.trim();
        setDomTaskId(nextTaskId);
        dispatch(
          setAdmissionFormContext({
            taskId: nextTaskId,
            applicantId: null,
          }),
        );
      }

      if (target.id === "student_id") {
        dispatch(setAdmissionFormContext({ applicantId: parsePositiveId(target.value) }));
      }
    };

    document.addEventListener("input", onInput);

    return () => {
      syncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      document.removeEventListener("input", onInput);
    };
  }, [location.pathname, dispatch, readTaskIdFromDom]);

  const handleEventTab = (id: string) => {
    setCurrentTab(id);
    const tabObj = admissionsOfficeHeaderTabsConst.find((tab) => tab.id === id);
    if (tabObj && tabObj.mass.length > 0) {
      const firstOption = tabObj.mass[0];
      setCurrentSelect(firstOption.name);
      dispatch(setTabsAdmissionsOffice(id));
      navigateToPage(id, firstOption.name);
    } else {
      dispatch(setTabsAdmissionsOffice(id));
      navigateToPage(currentTab, currentSelect);
    }
  };

  const handleChangeEvent = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setCurrentSelect(value);
    navigateToPage(currentTab, value);
  };

  const navigateToPage = (tab: string, select: string) => {
    const tabObj = admissionsOfficeHeaderTabsConst.find((t) => t.id === tab);
    const option = tabObj?.mass.find((o) => o.name === select);
    const slug = option?.slug || "admission";
    const lang = (option?.lang ?? "en") as AdmissionsLang;
    const path = lang === "en" ? `/admissions-office/${tab}/${slug}` : `/admissions-office/${lang}/${tab}/${slug}`;
    navigate(path);
  };

  const refreshCurrentStudentUserInfo = useCallback(async () => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (userInfo) setCurrentDataPopUp(userInfo);
      return userInfo;
    } catch (error) {
      console.log("applicant request error:", error);
      return undefined;
    }
  }, [currentStudentId]);

  const handleUserInfoClick = async () => {
    const normalizedTaskId = readTaskIdFromDom() || effectiveTaskId.trim();
    if (!normalizedTaskId) return;

    dispatch(setIsPreloyder(true));
    try {
      const task = await tasksPageRequests.getById(normalizedTaskId);
      if (!task) {
        notify({
          title: "Cannot open User Info",
          message: "Task was not found for this Task Id.",
          type: "error",
        });
        return;
      }

      const applicantInfo = parseTaskApplicantInfo(task.applicant_info);
      const resolvedApplicantId = parsePositiveId(parseTaskApplicantId(applicantInfo, task.applicant_id));

      if (resolvedApplicantId == null) {
        notify({
          title: "Cannot open User Info",
          message: "Applicant ID is missing for this Task Id.",
          type: "error",
        });
        return;
      }

      const resolvedTaskRowId = parsePositiveId(task.id) ?? parsePositiveId(normalizedTaskId);

      dispatch(
        setAdmissionFormContext({
          taskId: normalizedTaskId,
          applicantId: resolvedApplicantId,
        }),
      );
      setCurrentStudentId(resolvedApplicantId);
      setCurrentApplyInfoId(resolvedTaskRowId ?? undefined);

      const userInfo = await fetchApplicantUserInfo(resolvedApplicantId);
      if (!userInfo) {
        notify({
          title: "Cannot open User Info",
          message: "Failed to load applicant information.",
          type: "error",
        });
        return;
      }

      setCurrentDataPopUp(userInfo);
      setIsApPopUpFullName(true);
    } catch (error) {
      console.log("applicant request error:", error);
      notify({
        title: "Cannot open User Info",
        message: "Failed to load applicant information.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  return (
    <>
      <div className="header-admissions-office">
        <div className="header-bottom-tabs">
          {admissionsOfficeHeaderTabsConst.map((tab) => (
            <div
              className={`header-bottom-tab ${tab.id === currentTab ? "active" : ""}`}
              key={tab.id}
              onClick={() => handleEventTab(tab.id)}
            >
              <BtnDefault>
                <p>{tab.title}</p>
              </BtnDefault>
            </div>
          ))}
        </div>
        <VerticalLine />
        {location.pathname !== "/documents-qr-code" && (
          <>
            <div className="header-bottom-select">
              <SelectDefault onChange={handleChangeEvent} value={currentSelect}>
                {admissionsOfficeHeaderTabsConst
                  .find((item) => item.id === currentTab)
                  ?.mass.map((option, index) => (
                    <option key={index} value={option.name}>
                      {option.name}
                    </option>
                  ))}
              </SelectDefault>
            </div>
            <VerticalLine />
          </>
        )}
        <Link to={"/documents-qr-code"}>
          <BtnDefault reverse={true}>
            <p>Documents QR Code</p>
            <DatabaseSvg />
          </BtnDefault>
        </Link>
        <div className="header-bottom-buttons">
          <BtnDefault>
            <p>New document</p>
            <Refresh />
          </BtnDefault>
          <VerticalLine />
          <Link to={"/admissions-office-database"}>
            <BtnDefault reverse={true}>
              <p>Database</p>
              <DatabaseSvg />
            </BtnDefault>
          </Link>
          <VerticalLine />
          <BtnDefault disabled={isUserInfoDisabled} onClick={handleUserInfoClick}>
            <p>User Info</p>
          </BtnDefault>
        </div>
      </div>

      <CustomFullNamePopUp
        isApPopUpFullName={isApPopUpFullName}
        setIsApPopUpFullName={setIsApPopUpFullName}
        currentDataPopUp={currentDataPopUp}
        currentStudentId={currentStudentId}
        currentApplyInfoId={currentApplyInfoId}
        tableContext="tasks"
        onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
      />
    </>
  );
};

export default HeaderAdmissionsOffice;
